import Feature from 'ol/Feature';
import GeoJSONFormat from 'ol/format/GeoJSON';
import { LineString } from 'ol/geom';
import { bbox as bboxLoadingStrategy } from 'ol/loadingstrategy';
import VectorSource from 'ol/source/Vector';

import { getLayerFeatures } from 'cadenza/api-client/vector-layer-api';
import { context } from 'cadenza/map/context';
import { FeatureProperty } from 'cadenza/map/feature-property';
import { LayerProperty } from 'cadenza/map/layer-property';
import { SourceProperty } from 'cadenza/map/source-property';
import { FeatureLabelStyleResolver } from 'cadenza/map/style/feature-label-style-resolver';
import { FeatureStyleResolver } from 'cadenza/map/style/feature-style-resolver';
import { LINE_TO_PREDECESSOR_STYLE } from 'cadenza/map/timeline-visualization/timeline-constants';
import { createArrowToPreviousCoordinate } from 'cadenza/map/timeline-visualization/timeline-utilities';
import { ViewProperty } from 'cadenza/map/view-property';
import { assert } from 'cadenza/utils/custom-error';
import { logger } from 'cadenza/utils/logging';

const LABELS_COUNT_LIMIT_CROSSED_MESSAGE = 'layer.warning.labelsCountLimitCrossed';

export const VECTOR_LAYER_SOURCE_FEATURES_LOADED = 'featuresLoaded';

const LOADING_STRATEGIES_FACTORY = {
  BOUNDING_BOX: () => bboxLoadingStrategy,
  MAX_LAYER_EXTENT: (map, layerId) => maxLayerExtentStrategy(map, layerId)
};
const DEFAULT_LOADING_STRATEGY = 'BOUNDING_BOX';

function maxLayerExtentStrategy (map, layerId) {
  return () => {
    const layer = context(map).getLayer(layerId);
    const layerSuggestedExtent = layer.get(LayerProperty.SUGGESTED_EXTENT);
    const mapMaxExtent = map.getView().get(ViewProperty.MAX_EXTENT);

    return [ layerSuggestedExtent || mapMaxExtent ];
  };
}

/**
 * Vector layer source loads the styled features from the server to render them in the browser.
 */
export class VectorLayerSource extends VectorSource {

  #map;
  #layerRenderBuffer;
  _url;
  _areTimelinePointsConnectedWithLines;
  _featureAttributes;
  _labelAttributeNames;
  _cachedZoom = { zoom: -1 };

  constructor (map, layerId, {
    url,
    loadingStrategy = DEFAULT_LOADING_STRATEGY,
    projection,
    layerRenderBuffer = 0,
    areTimelinePointsConnectedWithLines,
    timelineTimestampAttributeName,
    labelAttributeNames = [],
    labelCountLimit = 500
  } = {}) {
    const strategy = LOADING_STRATEGIES_FACTORY[loadingStrategy](map, layerId);
    assert(strategy, `Unknown loading strategy ${loadingStrategy}`);
    assert(url, 'Missing vector layer source url');
    super({
      format: new GeoJSONFormat(),
      loader: extent => this._loadFeatures(extent),
      strategy,
      projection
    });
    this.#map = map;
    this.#layerRenderBuffer = layerRenderBuffer;
    this._url = url;
    this._areTimelinePointsConnectedWithLines = areTimelinePointsConnectedWithLines;
    this._labelAttributeNames = labelAttributeNames;
    this._labelCountLimit = labelCountLimit;
    this._featureAttributes = [ timelineTimestampAttributeName, ...labelAttributeNames ].filter(Boolean);
    if (this._areTimelinePointsConnectedWithLines) {
      const view = map.getView();
      view.on('change', () => {
        this._cachedZoom.zoom = Math.round(view.getZoom());
      });
    }
  }

  getUrl () {
    return this._url;
  }

  setUrl (url, silent = false) {
    if (url === this._url) {
      return;
    }
    this._url = url;
    if (!silent) {
      this.refresh();
    }
  }

  async _loadFeatures (extent) {
    const srsCode = this.#map.getView().getProjection().getCode();
    const mapSize = [ ...this.#map.getSize() ];
    mapSize[0] += this.#layerRenderBuffer * 2;
    mapSize[1] += this.#layerRenderBuffer * 2;

    try {
      let { result: res, messages } = await getLayerFeatures({
        url: this._url,
        srsCode,
        mapSize,
        extent,
        featureAttributes: this._featureAttributes
      });

      const { features, message } = await createOlFeatures(
        res,
        this.getFormat(),
        this._labelAttributeNames,
        this._labelCountLimit
      );
      if (this._areTimelinePointsConnectedWithLines) {
        const lineWithArrowsFeatures = getLineWithArrowsFeatures(res.features, this.getFormat(), this.#map, this._cachedZoom);
        features.push(...lineWithArrowsFeatures);
      }
      // Merge messages
      if (messages && message) {
        messages = [ ...messages, message ];
      } else if (message) {
        messages = [ message ];
      }

      this.clear(true);
      this.addFeatures(features);
      this.set(SourceProperty.MESSAGES, messages);
      this._dispatchFeaturesLoaded();
    } catch (error) {
      logger.error('Failed to get vector layer features', error);
      this.removeLoadedExtent(extent);
    }
  }

  _dispatchFeaturesLoaded () {
    this.dispatchEvent(VECTOR_LAYER_SOURCE_FEATURES_LOADED);
  }

}

async function createOlFeatures (
  {
    styles = [],
    features = [],
    labelStyles = []
  },
  format,
  labelAttributeNames,
  labelCountLimit) {
  const styleResolver = new FeatureStyleResolver(styles);
  const labelStyleResolver = new FeatureLabelStyleResolver(labelStyles);
  const maxLabelCountChecker = new MaxLabelCountChecker(labelCountLimit);

  const olFeatures = await Promise.all(features.map(async ({
    featureId,
    geometry,
    attributes,
    style,
    labelStyle,
    labelPositions,
    labelRotations
  }) => {
    // OL Feature
    const feature = new Feature({
      geometry: format.readGeometryFromObject(geometry)
    });
    feature.setId(featureId);

    // Main Style
    const mainStyle = styleResolver.resolve(style, feature.getGeometry().getType());

    // Attributes
    if (attributes) {
      feature.setProperties(attributes, true);
    }

    // Set main style if label style cannot or must not be rendered
    if (!isLabelRenderable(labelStyle, attributes)) {
      feature.setStyle(mainStyle);
      return feature;
    }
    // Increase label count
    // Each position results in a label, thus we use labelPositions.length for the counter.
    // We cannot use olFeatureLabelStyles.length since it is resolved asynchronously,
    // but we require it earlier.
    maxLabelCountChecker.add(labelPositions.length);
    if (maxLabelCountChecker.isLimitReached()) {
      feature.setStyle(mainStyle);
      return feature;
    }

    // Label Style
    const zIndex = getMainStyle(feature, mainStyle).getZIndex() + 1 ?? 0;

    feature.setStyle([
      mainStyle,
      ...createLabelStyles(labelStyleResolver, {
        attributes,
        labelStyle,
        labelAttributeNames,
        labelPositions,
        labelRotations,
        zIndex
      })
    ]);
    feature.set(FeatureProperty.IS_LABELED, true);
    return feature;
  }));

  let message;
  if (maxLabelCountChecker.isLimitReached()) {
    message = { type: 'WARNING', code: LABELS_COUNT_LIMIT_CROSSED_MESSAGE };
  }

  return {
    features: olFeatures,
    message
  };
}

function createLabelStyles (labelStyleResolver, {
  attributes,
  labelStyle,
  labelAttributeNames,
  labelPositions,
  labelRotations,
  zIndex
}) {
  // Feature has attributes, we must generate labels
  if (labelPositions && labelRotations) {
    assert(labelPositions.length === labelRotations.length, 'Label position count should match label rotations count');
  }
  assert(areAllAttributesPresent(labelAttributeNames, attributes), 'Inconsistent attributes');

  return labelPositions.map((position, index) => {
    return labelStyleResolver.resolve({
      labelStyleReference: labelStyle.$ref,
      attributes,
      position,
      rotation: labelRotations[index],
      zIndex
    });
  });
}

function isLabelRenderable (labelStyle, attributes) {
  return labelStyle && attributes && Object.keys(attributes).length;
}

function hasProperty (object, property) {
  return Object.prototype.hasOwnProperty.call(object, property);
}

function areAllAttributesPresent (labelAttributeNames, attributes) {
  return labelAttributeNames.every(name => hasProperty(attributes, name));
}

function getLineWithArrowsFeatures (featuresDto, format, map, cachedZoom) {
  const imageCache = new Map();
  const lineStringCoords = [];
  const features = [];
  for (let index = 0; index < featuresDto.length; index++) {
    const pointCoordinates = format.readGeometryFromObject(featuresDto[index].geometry).getCoordinates();
    lineStringCoords.push(pointCoordinates);
    if (index > 0) {
      const previousPoint = { coordinate: lineStringCoords[index - 1] };
      const endPoint = { coordinate: pointCoordinates };
      features.push(createArrowToPreviousCoordinate(previousPoint, endPoint, imageCache, map, cachedZoom));
    }
  }
  if (lineStringCoords.length) {
    const lineFeature = new Feature({
      geometry: new LineString(lineStringCoords)
    });
    lineFeature.setStyle(LINE_TO_PREDECESSOR_STYLE);
    features.push(lineFeature);
  }
  return features;
}

function getMainStyle (feature, olStyle) {
  let style = olStyle;
  if (typeof style === 'function') {
    style = style(feature);
  }
  if (Array.isArray(style)) {
    style = style[0];
  }
  return style;
}

class MaxLabelCountChecker {

  _maxLabelCount;
  _currentLabelCount = 0;

  constructor (maxLabelCount) {
    this._maxLabelCount = maxLabelCount;
  }

  add (amount) {
    this._currentLabelCount += amount;
  }

  isLimitReached () {
    return this._currentLabelCount >= this._maxLabelCount;
  }

}
